<template>
  <!-- Frequency -->
  <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
    <div class="col-span-2">
      <h5 class="font-semibold text-grey-light-900">Frequency</h5>
      <p class="text-grey-light-600 text-sm">Set controls around the frequency and limits of order payments.</p>
    </div>
    <div class="col-span-2">
      <div class="flex flex-col gap-4">
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="frequency.allowMonthlyPayments">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Allow monthly payments</span>
                  <span class="text-grey-light-600 text-sm">Allow users to pay instalments on a monthly basis.</span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="frequency.allowDefaultMinMonths">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :disabled="!isAdmin" :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Default Minimum Months</span>
                  <span class="text-grey-light-600 text-sm">
                    Set the minimum number of months as the default for all payment plans. Only Admins can update this
                    setting.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div class="grid grid-cols-2 gap-4" v-show="form.values?.frequency?.allowDefaultMinMonths">
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="frequency.defaultMinMonths">
              <FormItem>
                <Select :disabled="!isAdmin" v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select minimum months" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="i in 36" :key="i" :value="i.toString()">
                        {{ i }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="frequency.allowDefaultMaxMonths">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :disabled="!isAdmin" :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Default Maximum Months</span>
                  <span class="text-grey-light-600 text-sm">
                    Set the maximum number of months as the default for all payment plans. Only Admins can update this
                    setting.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div class="grid grid-cols-2 gap-4" v-show="form.values?.frequency?.allowDefaultMaxMonths">
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="frequency.defaultMaxMonths">
              <FormItem>
                <Select :disabled="!isAdmin" v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select maximum months" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="i in 36" :key="i" :value="i.toString()">
                        {{ i }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
    </div>
  </div>
  <SectionDivider class="my-4" />
  <!-- Payment plans -->
  <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
    <div class="col-span-2">
      <h5 class="font-semibold text-grey-light-900">Payment Plans</h5>
      <p class="text-grey-light-600 text-sm">Customize how your orders are processed and handled.</p>
    </div>
    <div class="col-span-2">
      <div class="flex flex-col gap-4">
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="orders.allowSplitPayments">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Allow split payments</span>
                  <span class="text-grey-light-600 text-sm">
                    Allow users to split the payments amongst more than one person.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="orders.suspendOnCancel">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Suspend on cancel</span>
                  <span class="text-grey-light-600 text-sm">Suspend the payment plan instead of cancelling.</span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="orders.orderExpiry">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Order expiry</span>
                  <span class="text-grey-light-600 text-sm">
                    Time that the customer has after setting up the payment plant to pay the deposit.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div v-show="isOrderExpiryEnabled" class="grid grid-cols-2 gap-4">
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="orders.timeValue">
              <FormItem>
                <FormControl>
                  <Input type="number" placeholder="Enter Value" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="orders.timeUnit">
              <FormItem>
                <Select v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the unit of time" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
        <FormField v-slot="{ value, handleChange }" name="orders.minimumOrderValue">
          <FormItem>
            <FormControl>
              <CurrencyInput
                :modelValue="value"
                @update:modelValue="handleChange"
                :min="0"
                :max="10000000"
                label="Minimum order value"
                :hide-error="true"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, handleChange }" name="orders.maximumOrderValue">
          <FormItem>
            <FormControl>
              <CurrencyInput
                :modelValue="value"
                @update:modelValue="handleChange"
                :min="0"
                :max="10000000"
                label="Maximum order value"
                :hide-error="true"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </div>
  </div>
  <SectionDivider class="my-4" />
  <!-- Deposit -->
  <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
    <div class="col-span-2">
      <h5 class="font-semibold text-grey-light-900">Deposit</h5>
      <p class="text-grey-light-600 text-sm">These are notifications to remind you of updates you might have missed.</p>
    </div>
    <div class="col-span-2">
      <div class="flex flex-col gap-4">
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="depositSettings.allowDefaultDepositType">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" :disabled="!isAdmin" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Allow Default Deposit Type</span>
                  <span class="text-grey-light-600 text-sm">
                    Set the default deposit type and amount of month for all payment plans. Only admins can update this
                    setting.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div v-if="form.values?.depositSettings?.allowDefaultDepositType" class="grid grid-cols-2 gap-4">
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="depositSettings.defaultDepositType">
              <FormItem>
                <FormLabel>Deposit type</FormLabel>
                <Select :disabled="!isAdmin" v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select deposit type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                      <SelectItem value="INSTALMENT">First Instalment</SelectItem>
                      <SelectItem value="FLAT">Flat</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <div class="col-span-1" v-show="form.values?.depositSettings?.defaultDepositType === 'PERCENTAGE'">
            <FormField v-slot="{ componentField }" name="depositSettings.defaultDepositTypePercentage">
              <FormItem>
                <FormLabel>Select Percentage</FormLabel>
                <Select :disabled="!isAdmin" v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Percentage" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="i in 100" :key="i" :value="i.toString()">{{ i }}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <div class="col-span-1" v-show="form.values?.depositSettings?.defaultDepositType === 'FLAT'">
            <FormField v-slot="{ componentField }" name="depositSettings.defaultDepositTypeFlatFee">
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl>
                  <Input :disabled="!isAdmin" type="text" placeholder="200" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="depositSettings.allowDefaultOverride">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :disabled="!isAdmin" :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Allow default deposit override</span>
                  <span class="text-grey-light-600 text-sm">
                    All non-admin users to override the default deposit type when creating payment plans.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </div>
  </div>
  <SectionDivider class="my-4" />
  <div>
    <h1 class="font-semibold text-xl text-grey-light-900">Cancellations</h1>
    <p class="text-grey-light-600 text-sm">The fee users are charged when they cancel their payment plan.</p>
  </div>
  <SectionDivider class="my-4" />
  <!-- Cancellation Fees -->
  <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
    <div class="col-span-2">
      <h5 class="font-semibold text-grey-light-900">Cancellation Fees</h5>
      <p class="text-grey-light-600 text-sm">Set cancellation fees for payment plans.</p>
    </div>
    <div class="col-span-2">
      <div class="flex flex-col gap-4">
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="cancellationFees.deposit">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent
                  :model-value="value"
                  @update:model-value="handleChange"
                  data-testid="deposit-checkbox"
                />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Deposit</span>
                  <span class="text-grey-light-600 text-sm">
                    The full deposit will be charged as a cancellation fee, along with any other applicable charges.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="cancellationFees.percentage">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent
                  :model-value="value"
                  @update:model-value="handleChange"
                  :disabled="form.values.cancellationFees.deposit"
                />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Percentage</span>
                  <span class="text-grey-light-600 text-sm">
                    The percentage will be charged as a cancellation fee, along with any other applicable charges.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div v-if="isPercentageEnabled" class="grid grid-cols-2 gap-4">
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="cancellationFees.percentageType">
              <FormItem>
                <Select v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="totalOrderValue">Total order value</SelectItem>
                      <SelectItem value="totalPaidValue">Total paid value</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <div class="col-span-1">
            <FormField v-slot="{ componentField }" name="cancellationFees.percentageValue">
              <FormItem>
                <Select v-bind="componentField">
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Percentage" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="0">0</SelectItem>
                      <SelectItem v-for="i in 100" :key="i" :value="i.toString()">{{ i }}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="cancellationFees.flatFee">
          <FormItem>
            <div class="flex gap-2">
              <FormControl>
                <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
              </FormControl>
              <FormLabel>
                <div class="flex flex-col gap-2">
                  <span class="font-medium">Flat Fee</span>
                  <span class="text-grey-light-600 text-sm">
                    A fixed fee charged on cancellation, in addition to any other applicable charges.
                  </span>
                </div>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
        <div v-if="isFlatFeeEnabled">
          <FormField v-slot="{ value, handleChange }" name="cancellationFees.flatFeeValue">
            <FormItem>
              <FormControl>
                <CurrencyInput
                  :modelValue="value"
                  @update:modelValue="handleChange"
                  :min="0"
                  :max="10000000"
                  label=""
                  :hide-error="true"
                  :hide-label="true"
                />
                <span class="text-grey-light-400 text-xs">Amount between R0 - R100k</span>
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
      </div>
    </div>
  </div>
  <SectionDivider class="my-4" />
  <!-- Auto Cancellations | Hide the entire section if the feature is not enabled -->
  <div v-if="settings.features?.enableAutoCancellation" class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
    <div class="col-span-2">
      <h5 class="font-semibold text-grey-light-900">Auto Cancellation</h5>
      <p class="text-grey-light-600 text-sm">Set conditions for automatic payment plan cancellation.</p>
    </div>
    <div class="col-span-2">
      <div class="flex flex-col gap-4">
        <FormField v-slot="{ value, handleChange }" name="autoCancellations.autoCancellation">
          <FormItem class="flex gap-2 items-center" data-testid="auto-cancellation-switch">
            <FormControl>
              <SwitchComponent :model-value="value" @update:model-value="handleChange" />
            </FormControl>
            <FormLabel class="!mt-0">
              <div class="flex flex-col gap-2">
                <span class="font-medium">Allow auto cancellation</span>
                <span class="text-grey-light-600 text-sm">Automatically cancel payment plans.</span>
              </div>
            </FormLabel>
          </FormItem>
          <FormMessage />
        </FormField>
        <template v-if="isAutoCancellationEnabled">
          <FormField v-slot="{ value, handleChange }" type="checkbox" name="autoCancellations.gracePeriod">
            <FormItem>
              <div class="flex gap-2">
                <FormControl>
                  <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Grace Period</span>
                    <span class="text-grey-light-600 text-sm">
                      The time that you give the user to make the necessary payments after informing them that their
                      plan will be cancelled if they do not.
                    </span>
                  </div>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          </FormField>
          <div v-if="isGracePeriodEnabled" class="grid grid-cols-2 gap-4">
            <div class="col-span-1">
              <FormField v-slot="{ componentField }" name="autoCancellations.gracePeriodValue">
                <FormItem>
                  <FormLabel>Grace Period Days</FormLabel>
                  <Select v-bind="componentField">
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <!-- Grace period is stored in hours so values are multiplied by 24 -->
                        <SelectItem value="0">0</SelectItem>
                        <SelectItem v-for="i in 30" :key="i" :value="`${i * 24}`">{{ i }}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>
          <FormField v-slot="{ value, handleChange }" type="checkbox" name="autoCancellations.missedPayments">
            <FormItem>
              <div class="flex gap-2">
                <FormControl>
                  <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Missed payments</span>
                    <span class="text-grey-light-600 text-sm">
                      Automatically cancel a payment plan if the customer misses a payment.
                    </span>
                  </div>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          </FormField>
          <div v-if="isMissedPaymentsEnabled" class="grid grid-cols-2 gap-4">
            <div class="col-span-1">
              <FormField v-slot="{ componentField }" name="autoCancellations.numberOfMissedPayments">
                <FormItem>
                  <FormLabel>No. of payments missed</FormLabel>
                  <Select v-bind="componentField">
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem v-for="i in 6" :key="i" :value="i.toString()">{{ i }}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
          </div>
          <FormField v-slot="{ value, handleChange }" type="checkbox" name="autoCancellations.finalPaymentMissed">
            <FormItem>
              <div class="flex gap-2">
                <FormControl>
                  <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Final Payment Missed</span>
                    <span class="text-grey-light-600 text-sm">
                      Cancellation will occur if the Last Payment of the payment plan is missed.
                    </span>
                  </div>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField v-slot="{ value, handleChange }" type="checkbox" name="autoCancellations.timeBased">
            <FormItem>
              <div class="flex gap-2">
                <FormControl>
                  <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                </FormControl>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Time Based</span>
                    <span class="text-grey-light-600 text-sm">
                      Configure a percentage of an order's payment plan that must be achieved by a specific date.
                    </span>
                  </div>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          </FormField>
          <template v-if="isTimeBasedEnabled">
            <FormField v-slot="{ value, handleChange }" name="autoCancellations.percentageComplete">
              <FormItem>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Percentage Complete</span>
                    <span class="text-grey-light-600 text-sm">
                      The percentage of the payment plan that must be paid before the next interval.
                    </span>
                  </div>
                </FormLabel>
                <FormControl>
                  <div class="max-w-28">
                    <NumberInput
                      :model-value="value"
                      @update:modelValue="handleChange"
                      :min="0"
                      :max="100"
                      :hide-label="true"
                      :hide-error="true"
                    />
                  </div>
                </FormControl>
              </FormItem>
              <FormMessage />
            </FormField>
            <FormField v-slot="{ value, handleChange }" name="autoCancellations.daysBeforeFinalPayment">
              <FormItem>
                <FormLabel>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">Days before final payment date</span>
                    <span class="text-grey-light-600 text-sm">
                      The number of days before the final payment date that defines the interval period.
                    </span>
                  </div>
                </FormLabel>
                <FormControl>
                  <div class="max-w-28">
                    <NumberInput
                      icon="calendar"
                      :model-value="value"
                      @update:modelValue="handleChange"
                      :min="0"
                      :max="100"
                      :hide-label="true"
                      :hide-error="true"
                    />
                  </div>
                </FormControl>
              </FormItem>
              <FormMessage />
            </FormField>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import NumberInput from '@/components/common/NumberInput.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { useUserStore } from '@/stores/users';
import { storeToRefs } from 'pinia';
import { type FormContext } from 'vee-validate';
import { computed, watch } from 'vue';
import type { ModelMerchantRequest } from '@layup/api-sdk';

const { form, settings } = defineProps<{
  form: FormContext<any, any>;
  settings: ModelMerchantRequest;
}>();

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const isAdmin = computed(() => user.value.merchantRole === 'ADMIN');
const isOrderExpiryEnabled = computed(() => form?.values?.orders?.orderExpiry);

// Cancellation fees
const isPercentageEnabled = computed(() => form?.values?.cancellationFees?.percentage);
const isFlatFeeEnabled = computed(() => form?.values?.cancellationFees?.flatFee);

// Auto cancellations
const isMissedPaymentsEnabled = computed(() => form?.values?.autoCancellations?.missedPayments);
const isGracePeriodEnabled = computed(() => form?.values?.autoCancellations?.gracePeriod);
const isAutoCancellationEnabled = computed(() => form?.values?.autoCancellations?.autoCancellation);
// const isFinalPaymentMissedEnabled = computed(() => form?.values?.autoCancellations?.finalPaymentMissed);
const isTimeBasedEnabled = computed(() => form?.values?.autoCancellations?.timeBased);

watch(
  () => form.values.cancellationFees?.deposit,
  (newValue) => {
    if (newValue) {
      form.setFieldValue('cancellationFees.percentage', false);
      form.setFieldValue('cancellationFees.percentageValue', '0');
    }
  },
);

watch(
  () => form?.values?.cancellationFees?.percentage,
  (newValue) => {
    if (newValue) {
      if (settings?.cancellationTerms?.definition) {
        form.setFieldValue(
          'cancellationFees.percentageType',
          settings?.cancellationTerms?.definition[0].policy?.definition?.percentageShouldUseTotalPaid
            ? 'totalPaidValue'
            : 'totalOrderValue',
        );
        form.setFieldValue(
          'cancellationFees.percentageValue',
          settings?.cancellationTerms?.definition[0].policy?.definition?.percentage?.toString() || '0',
        );
      }
    }
  },
);

watch(
  () => form?.values?.cancellationFees?.flatFee,
  (newValue) => {
    if (newValue) {
      const flatFee = settings.cancellationTerms?.definition?.[0]?.policy?.definition?.flatFee;
      if (flatFee != null) {
        form.setFieldValue('cancellationFees.flatFeeValue', flatFee);
      }
    }
  },
);

watch(
  () => form.values.autoCancellations?.missedPayments,
  (newValue) => {
    if (newValue) {
      form.setFieldValue('autoCancellations.finalPaymentMissed', false);
      form.setFieldValue('autoCancellations.timeBased', false);
      form.setFieldValue(
        'autoCancellations.numberOfMissedPayments',
        Number(settings?.cancellationTerms?.definition?.[0]?.type?.definition?.missed || 1).toString(),
      );
    }
  },
);

// Final payment forces percentage complete to 100% and days before final payment to 0
watch(
  () => form?.values?.autoCancellations?.finalPaymentMissed,
  (newValue) => {
    if (newValue) {
      // Disabled other cancellation types
      form.setFieldValue('autoCancellations.missedPayments', false);
      form.setFieldValue('autoCancellations.timeBased', false);
      if (settings.cancellationTerms?.definition) {
        form.setFieldValue('autoCancellations.percentageComplete', 100);
        form.setFieldValue('autoCancellations.daysBeforeFinalPayment', 0);
      }
    }
  },
);

// Time based lets the user set the percentage complete and days before final payment
watch(
  () => form?.values?.autoCancellations?.timeBased,
  (newValue) => {
    if (newValue) {
      form.setFieldValue('autoCancellations.missedPayments', false);
      form.setFieldValue('autoCancellations.finalPaymentMissed', false);

      if (settings.cancellationTerms?.definition) {
        const percentage = settings.cancellationTerms.definition[0].type?.definition?.percentage || 0;
        const end = settings.cancellationTerms.definition[0].end || 0;
        form.setFieldValue('autoCancellations.percentageComplete', percentage);
        form.setFieldValue('autoCancellations.daysBeforeFinalPayment', Math.abs(end) > 0 ? Math.abs(end) : 0);
      }
    }
  },
);
</script>
